import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Check, ArrowLeft, CreditCard, Smartphone, Phone, Zap, Film, Building2, Loader2, X, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { authFetch } from '../lib/authFetch';
import {
  PLANS, PLAN_LIST, TIER_ORDER, FOUNDING_CAP, formatKES, foundingClaimedLabel,
  SUPPORTED_CURRENCIES, detectDefaultCurrency, rememberCurrency, formatMoney, planPriceMinor,
} from '../lib/pricing';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = { spark: Zap, indie: Film, studio: Building2, founding: Crown };
const STYLE = {
  spark:    { color: 'text-muted-foreground', border: 'border-border/40' },
  indie:    { color: 'text-foreground',       border: 'border-border/60' },
  studio:   { color: 'text-purple-400',       border: 'border-purple-400/50' },
  founding: { color: 'text-yellow-400',       border: 'border-yellow-400/70 neon-border' },
};

export default function UpgradeToPro() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null); // plan key or null
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [founding, setFounding] = useState(null); // { cap, claimed, sold_out }
  const [paymentMethod, setPaymentMethod] = useState('mobile_money');
  const [phoneNumber, setPhoneNumber] = useState('');
  // Charged currency. The default is only a locale GUESS (Kenya → KES, else
  // USD) — the buyer always sees the toggle and confirms/switches before
  // checkout; the confirmed value is what the checkout POST carries.
  const [currency, setCurrency] = useState(detectDefaultCurrency);
  const [catalog, setCatalog] = useState(null); // /payments/catalog
  // Plan the user committed to (clicked a button). Drives the dedicated
  // "Pay with M-Pesa" button rendered below the phone field.
  const [selectedPlan, setSelectedPlan] = useState(null);
  const phoneInputRef = useRef(null);

  // M-Pesa STK push status: 'idle' | 'pending' | 'success' | 'failed' | 'timeout'
  const [mpesaStatus, setMpesaStatus] = useState('idle');
  const [mpesaReference, setMpesaReference] = useState(null);
  const [mpesaMessage, setMpesaMessage] = useState('');
  const pollTimerRef = useRef(null);
  const pollDeadlineRef = useRef(0);

  const refreshFounding = () =>
    fetch(`${API}/founding/status`).then(r => r.json()).then(setFounding).catch(() => {});

  useEffect(() => {
    authFetch(`${API}/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(setUser)
      .catch(console.error);
    authFetch(`${API}/subscription/status`, { credentials: 'include' })
      .then(r => r.json())
      .then(setSubscription)
      .catch(() => {});
    refreshFounding();
    fetch(`${API}/payments/catalog`).then(r => r.json()).then(setCatalog).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USD subscriptions need their Paystack plan codes live; until the backend
  // says so, USD recurring buttons are disabled (Founding is one-time and
  // works in USD regardless).
  const usdRecurringReady = catalog?.currencies?.USD?.enabled === true;

  const switchCurrency = (cur) => {
    setCurrency(cur);
    rememberCurrency(cur);
  };

  // M-Pesa settles in KES only. This effect (not just the toggle handler)
  // owns the rule so the INITIAL state can never desync: a first-time
  // visitor whose locale guessed USD starts with paymentMethod
  // 'mobile_money' — without this, checkout would route down the M-Pesa
  // branch and charge KES after showing dollar prices.
  useEffect(() => {
    if (currency !== 'KES' && paymentMethod === 'mobile_money') setPaymentMethod('card');
  }, [currency, paymentMethod]);

  // Prices render from the backend catalog when it has loaded (the backend
  // is authoritative at charge time); the local table is the fallback so
  // the page never shows blanks on a failed fetch.
  const priceMinorFor = (planId) =>
    catalog?.currencies?.[currency]?.plans?.[planId]?.amount_minor
      ?? planPriceMinor(currency, planId);

  // Cleanup any in-flight poll when the page unmounts
  useEffect(() => () => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
  }, []);

  const stopPolling = () => {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const pollOnce = async (reference) => {
    try {
      const res = await authFetch(`${API}/payments/status/${reference}`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Status check failed');

      if (data.status === 'success') {
        stopPolling();
        setMpesaStatus('success');
        const isFounding = selectedPlan === 'founding';
        toast.success(isFounding ? 'Welcome, Founding member. Studio is yours for life.' : `Welcome to ${PLANS[data.tier]?.name || data.tier}.`);
        // Refresh user state so the rest of the app sees the new tier
        try {
          const me = await authFetch(`${API}/auth/me`, { credentials: 'include' }).then(r => r.json());
          setUser(me);
        } catch {}
        setTimeout(() => navigate('/dashboard'), 1500);
        return;
      }
      if (data.status === 'failed') {
        stopPolling();
        setMpesaStatus('failed');
        // `reason` is set when a confirmed payment could not be applied
        // (e.g. every Founding seat was claimed first) — the money is being refunded.
        setMpesaMessage(data.message || 'Payment was declined');
        if (data.reason) refreshFounding();
        return;
      }
      // Still pending — check the deadline, then schedule the next tick.
      // The hold is deliberately NOT released here: our 60s is not Paystack's
      // deadline, and the prompt can still be approved. Paystack's own
      // charge.failed (via the webhook) releases it when the prompt dies.
      if (Date.now() >= pollDeadlineRef.current) {
        stopPolling();
        setMpesaStatus('timeout');
        return;
      }
      pollTimerRef.current = setTimeout(() => pollOnce(reference), 5000);
    } catch (err) {
      console.error('Polling error:', err);
      // Soft-fail: keep trying until the deadline. Network blips shouldn't abort.
      if (Date.now() >= pollDeadlineRef.current) {
        stopPolling();
        setMpesaStatus('timeout');
        return;
      }
      pollTimerRef.current = setTimeout(() => pollOnce(reference), 5000);
    }
  };

  // Tell the server to give a founding seat hold back straight away. Without
  // this, Cancel was purely client-side and the seat stayed reserved for 40
  // minutes. Fire-and-forget: the UI must never wait
  // on it, and a failure here only means the hold expires on its own.
  const releaseMpesaHold = (reference) => {
    if (!reference) return;
    authFetch(`${API}/payments/mpesa/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ reference }),
    }).catch(() => {});
  };

  const cancelMpesa = () => {
    stopPolling();
    releaseMpesaHold(mpesaReference);
    setMpesaStatus('idle');
    setMpesaReference(null);
    setMpesaMessage('');
  };

  const isFoundingMember = subscription?.founding === true;
  const currentTier = isFoundingMember ? 'founding' : (user?.subscription_tier || 'spark');
  const currentTierIdx = TIER_ORDER.indexOf(isFoundingMember ? 'studio' : currentTier);
  const soldOut = founding?.sold_out === true;

  const handleUpgrade = async (planKey) => {
    if (!user?.email) { toast.error('Please login to continue'); return; }

    const plan = PLANS[planKey];
    const pid = plan?.planId;
    if (!pid) return;

    setSelectedPlan(planKey);

    // Effective method derived at CLICK time: M-Pesa exists only in KES, so
    // a non-KES currency always routes to card regardless of stale state.
    const method = currency !== 'KES' ? 'card' : paymentMethod;

    // M-Pesa selected but no number yet — guide the user down to the phone
    // field and focus it, instead of failing silently at the tier card.
    if (method === 'mobile_money' && !phoneNumber.trim()) {
      toast.info('Enter your M-Pesa number below');
      setTimeout(() => {
        phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        phoneInputRef.current?.focus();
      }, 50);
      return;
    }

    setLoading(planKey);
    try {
      if (method === 'card') {
        // Card flow: Paystack card endpoint → open authorization URL in new tab
        const res = await authFetch(`${API}/payments/paystack/initialize`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: user.email, plan: pid, currency }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (res.status === 409) refreshFounding();
          throw new Error(data.detail || 'Failed to initialize card payment');
        }
        window.open(data.authorization_url, '_blank', 'noopener');
        toast.info('Complete payment in the new tab — return here once done.');
        setLoading(null);
      } else {
        // M-Pesa flow — Paystack Charge API (mobile_money). Sends STK push;
        // we then poll /payments/status every 5s until success/failure/timeout.
        if (!phoneNumber.trim()) {
          toast.error('Please enter your M-Pesa phone number');
          setLoading(null);
          return;
        }
        const res = await authFetch(`${API}/payments/mpesa/charge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            plan: pid,
            email: user.email,
            phone_number: phoneNumber.trim(),
            currency: 'KES', // M-Pesa settles in KES only
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (res.status === 409) refreshFounding();
          throw new Error(data.detail || 'Failed to send M-Pesa STK push');
        }

        setMpesaReference(data.reference);
        setMpesaStatus('pending');
        setMpesaMessage('');
        setLoading(null);

        // 60s window matching the M-Pesa STK push lifetime, then 5s polling.
        pollDeadlineRef.current = Date.now() + 60_000;
        pollTimerRef.current = setTimeout(() => pollOnce(data.reference), 5000);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to start checkout');
      setLoading(null);
    }
  };

  // Button state per plan card.
  const buttonFor = (plan) => {
    if (plan.key === 'spark') return null;
    if (plan.key === 'founding') {
      if (isFoundingMember) return { label: 'Your plan — for life', disabled: true };
      if (soldOut) return { label: `All ${FOUNDING_CAP} seats claimed`, disabled: true };
      return { label: 'Claim a Founding seat', disabled: false };
    }
    if (isFoundingMember) return { label: 'Included in Founding', disabled: true };
    if (currentTier === plan.key) return { label: 'Current plan', disabled: true };
    if (TIER_ORDER.indexOf(plan.key) < currentTierIdx) return { label: 'Downgrade', disabled: true };
    // USD subscriptions wait on their Paystack plan codes (backend 503s
    // anyway — fail closed in the UI too, with a way forward).
    if (currency === 'USD' && !usdRecurringReady) {
      return { label: 'USD coming soon — pay in KES', disabled: true };
    }
    return { label: `Upgrade to ${plan.name}`, disabled: false };
  };

  const selected = selectedPlan ? PLANS[selectedPlan] : null;
  const selectedBtn = selected ? buttonFor(selected) : null;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />

      <header className="glassmorphism sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-btn"
            className="rounded-none font-mono uppercase tracking-widest text-xs px-2 sm:px-4"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Back to Dashboard</span>
          </Button>

          {/* Currency toggle — prices update live; the choice is confirmed
              here, never charged on the locale guess alone. */}
          <div className="flex border border-border/60" data-testid="currency-toggle">
            {SUPPORTED_CURRENCIES.map((cur) => (
              <button
                key={cur}
                onClick={() => switchCurrency(cur)}
                data-testid={`currency-${cur.toLowerCase()}`}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-widest transition-all ${
                  currency === cur
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                {cur}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/20 border border-primary/30 px-4 py-2 mb-6">
            <Crown className="w-5 h-5 text-primary" />
            <span className="font-mono text-primary uppercase tracking-wider text-sm">Plans & Pricing</span>
          </div>
          <h1 className="font-mono text-4xl md:text-5xl font-bold tracking-tighter mb-4">
            Build Better Stories
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {currency === 'KES'
              ? 'Priced in Kenyan shillings. Pay by M-Pesa or card. Cancel anytime.'
              : 'Priced in US dollars. Pay by card. Cancel anytime.'}
          </p>
          {currency !== 'KES' && (
            <p className="mt-2 text-xs font-mono text-muted-foreground" data-testid="charged-in-usd-note">
              You will be charged in USD. Your bank may apply its own conversion rate.
            </p>
          )}
          {currentTier !== 'spark' && (
            <p className="mt-3 text-sm font-mono text-primary" data-testid="current-plan-line">
              Current plan: <span className="uppercase">{isFoundingMember ? 'Founding — lifetime Studio' : currentTier}</span>
              {' — '}
              <button
                onClick={() => navigate('/settings')}
                className="underline text-muted-foreground hover:text-foreground transition-colors font-mono text-sm"
              >
                Manage subscription
              </button>
            </p>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {PLAN_LIST.map((plan) => {
            const Icon = ICONS[plan.key];
            const style = STYLE[plan.key];
            const isCurrent = currentTier === plan.key;
            const btn = buttonFor(plan);
            const isFounding = plan.key === 'founding';

            return (
              <div
                key={plan.key}
                data-testid={`tier-card-${plan.key}`}
                className={`bg-card border ${style.border} p-6 relative flex flex-col`}
              >
                {isFounding && !isCurrent && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-yellow-400 px-3 py-0.5">
                    <span className="font-mono text-xs uppercase tracking-wider text-black">
                      {soldOut ? 'Sold out' : 'Launch offer'}
                    </span>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 bg-primary px-3 py-0.5">
                    <span className="font-mono text-xs uppercase tracking-wider text-primary-foreground">Current</span>
                  </div>
                )}

                <div className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon className={`w-5 h-5 ${style.color}`} />
                    <h3 className={`font-mono text-xl font-semibold ${style.color}`}>{plan.name}</h3>
                  </div>
                  {plan.priceKES === 0 ? (
                    <div className="text-2xl font-bold font-mono">Free</div>
                  ) : (
                    <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold font-mono" data-testid={`price-kes-${plan.key}`}>
                        {formatMoney(currency, priceMinorFor(plan.planId))}
                      </span>
                      <span className="text-muted-foreground text-sm">{plan.oneTime ? 'once' : '/month'}</span>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground font-mono mt-1">{plan.tagline}</p>
                  {isFounding && (
                    <p className="text-xs font-mono mt-2 text-yellow-400" data-testid="founding-claimed">
                      {founding ? foundingClaimedLabel(founding.claimed, founding.cap) : `— of ${FOUNDING_CAP} claimed`}
                    </p>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs">
                      <Check className="w-3.5 h-3.5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                  {plan.locked.map((f, i) => (
                    <li key={`l${i}`} className="flex items-start gap-2 text-xs text-muted-foreground/50">
                      <Lock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {!btn ? (
                  <div className="py-3 text-center font-mono text-xs text-muted-foreground uppercase tracking-wider border border-border/30">
                    {isCurrent ? 'Your current plan' : 'Free forever'}
                  </div>
                ) : (
                  <Button
                    onClick={() => handleUpgrade(plan.key)}
                    disabled={btn.disabled || !!loading || mpesaStatus === 'pending'}
                    data-testid={`upgrade-btn-${plan.key}`}
                    className={`w-full rounded-none font-mono uppercase tracking-widest text-xs py-3 ${
                      isFounding
                        ? 'bg-yellow-400 text-black hover:bg-yellow-300'
                        : 'bg-primary text-primary-foreground hover:bg-primary/90'
                    }`}
                  >
                    {loading === plan.key ? 'Processing...' : btn.label}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Payment Method (for paid plans) */}
        <div className="max-w-md mx-auto">
          <p className="font-mono text-sm uppercase tracking-wider text-muted-foreground mb-3 text-center">
            Payment Method
          </p>
          <div className="flex border border-border/60 mb-4" data-testid="payment-method-toggle">
            <button
              onClick={() => setPaymentMethod('mobile_money')}
              data-testid="method-mpesa"
              disabled={currency !== 'KES'}
              title={currency !== 'KES' ? 'M-Pesa is KES only — switch currency to KES' : undefined}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 font-mono text-sm uppercase tracking-wider transition-all ${
                currency !== 'KES'
                  ? 'bg-card text-muted-foreground/40 cursor-not-allowed'
                  : paymentMethod === 'mobile_money' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              M-Pesa
            </button>
            <button
              onClick={() => setPaymentMethod('card')}
              data-testid="method-card"
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 font-mono text-sm uppercase tracking-wider transition-all ${
                paymentMethod === 'card' ? 'bg-primary text-primary-foreground' : 'bg-card text-muted-foreground hover:text-foreground'
              }`}
            >
              <CreditCard className="w-4 h-4" />
              Card
            </button>
          </div>

          {paymentMethod === 'mobile_money' && (
            <div data-testid="mpesa-phone-input">
              <label className="block font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">
                Safaricom Phone Number
              </label>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <Input
                  ref={phoneInputRef}
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="07XX XXX XXX"
                  data-testid="phone-number-input"
                  className="rounded-none font-mono"
                  disabled={mpesaStatus === 'pending'}
                />
              </div>
              {mpesaStatus === 'idle' && (
                <p className="text-xs text-muted-foreground mt-1 font-mono">
                  You will receive an M-Pesa STK push to confirm payment
                </p>
              )}

              {/* Dedicated pay button — appears once a plan is picked, so the
                  user doesn't have to scroll back up to the plan card. */}
              {selected && selectedBtn && !selectedBtn.disabled
                && mpesaStatus !== 'pending' && mpesaStatus !== 'success' && (
                <Button
                  onClick={() => handleUpgrade(selected.key)}
                  disabled={!!loading || mpesaStatus === 'pending'}
                  data-testid="mpesa-pay-btn"
                  className="w-full mt-4 rounded-none font-mono uppercase tracking-widest text-xs py-4 bg-yellow-400 text-black hover:bg-yellow-300 flex items-center justify-center gap-2"
                >
                  <Smartphone className="w-4 h-4 flex-shrink-0" />
                  {loading === selected.key
                    ? 'Sending STK push…'
                    : `Pay ${formatKES(selected.priceKES)} ${selected.oneTime ? 'once' : '/month'} for ${selected.name} via M-Pesa`}
                </Button>
              )}

              {/* Live STK-push status panel */}
              {mpesaStatus === 'pending' && (
                <div
                  className="mt-4 border border-primary/40 bg-card p-4 flex items-start gap-3"
                  data-testid="mpesa-status-pending"
                >
                  <Loader2 className="w-5 h-5 text-primary animate-spin flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-sm text-foreground">
                      Check your phone for the M-Pesa prompt
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 font-mono break-all">
                      Enter your PIN to approve. Ref: {mpesaReference}
                    </p>
                  </div>
                  <button
                    onClick={cancelMpesa}
                    aria-label="Cancel"
                    className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0"
                    data-testid="mpesa-cancel"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              {mpesaStatus === 'success' && (
                <div
                  className="mt-4 border border-primary/40 bg-primary/5 p-4 font-mono text-sm text-foreground"
                  data-testid="mpesa-status-success"
                >
                  Payment confirmed. Redirecting to your dashboard…
                </div>
              )}
              {mpesaStatus === 'failed' && (
                <div
                  className="mt-4 border border-destructive/40 bg-card p-4 font-mono text-sm"
                  data-testid="mpesa-status-failed"
                >
                  <p className="text-destructive">Payment not applied.</p>
                  {mpesaMessage && (
                    <p className="text-xs text-muted-foreground mt-1">{mpesaMessage}</p>
                  )}
                  <button
                    onClick={cancelMpesa}
                    className="mt-3 font-mono uppercase text-xs tracking-widest text-primary hover:text-foreground"
                  >
                    Try again
                  </button>
                </div>
              )}
              {mpesaStatus === 'timeout' && (
                <div
                  className="mt-4 border border-amber-500/40 bg-card p-4 font-mono text-sm"
                  data-testid="mpesa-status-timeout"
                >
                  <p className="text-amber-500">Payment not received — try again.</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    The STK push wasn't approved in time. You can retry now.
                  </p>
                  <button
                    onClick={cancelMpesa}
                    className="mt-3 font-mono uppercase text-xs tracking-widest text-primary hover:text-foreground"
                  >
                    Try again
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center justify-center gap-4 text-muted-foreground text-xs font-mono">
            <span className="flex items-center gap-1"><Smartphone className="w-3.5 h-3.5" /> M-Pesa</span>
            <span className="flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Visa / Mastercard</span>
          </div>
          <p className="text-center text-xs text-muted-foreground font-mono mt-2">
            Secure payment via Paystack. Monthly plans cancel anytime; Founding is a one-time payment.
          </p>
          <div className="mt-6 flex items-center justify-center gap-4 text-muted-foreground text-xs font-mono">
            <button onClick={() => navigate('/terms')} className="hover:text-foreground transition-colors underline">
              Terms of Service
            </button>
            <button onClick={() => navigate('/privacy')} className="hover:text-foreground transition-colors underline">
              Privacy Policy
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
